import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { Controller } from "react-hook-form";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";

import Dialog from "components/dialog";
import {
  TASK_PRIORITIES,
  TASK_PRIORITY_HIGH,
  TASK_PRIORITY_LOW,
  TASK_PRIORITY_MEDIUM,
  TASK_PRIORITY_NOT_SET,
  TASK_PRIORITY_URGENT
} from "constants/staff-tasks";

type BulkUpdatePriorityModalProps = {
  isOpen: boolean;
  selectedCount: number;
  onClose: () => void;
  onSave: (priority: number) => void;
  isLoading?: boolean;
};

export default function BulkUpdatePriorityModal({
  isOpen,
  selectedCount,
  onClose,
  onSave,
  isLoading = false
}: BulkUpdatePriorityModalProps) {
  const { control, handleSubmit, reset } = useForm<{ priority: number }>({
    defaultValues: {
      priority: TASK_PRIORITY_NOT_SET
    }
  });

  const onSubmit = useCallback(
    (data: { priority: number }) => {
      onSave(data.priority);
      reset();
    },
    [onSave, reset]
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <Dialog
      title={`Update Priority for ${selectedCount} task${selectedCount !== 1 ? "s" : ""}`}
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ p: 2 }}>
          <Controller
            name="priority"
            control={control}
            rules={{ required: "Priority is required" }}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select {...field} label="Priority">
                  <MenuItem value={TASK_PRIORITY_NOT_SET}>
                    {TASK_PRIORITIES.getFromCode(TASK_PRIORITY_NOT_SET)}
                  </MenuItem>
                  <MenuItem value={TASK_PRIORITY_LOW}>
                    {TASK_PRIORITIES.getFromCode(TASK_PRIORITY_LOW)}
                  </MenuItem>
                  <MenuItem value={TASK_PRIORITY_MEDIUM}>
                    {TASK_PRIORITIES.getFromCode(TASK_PRIORITY_MEDIUM)}
                  </MenuItem>
                  <MenuItem value={TASK_PRIORITY_HIGH}>
                    {TASK_PRIORITIES.getFromCode(TASK_PRIORITY_HIGH)}
                  </MenuItem>
                  <MenuItem value={TASK_PRIORITY_URGENT}>
                    {TASK_PRIORITIES.getFromCode(TASK_PRIORITY_URGENT)}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
